// Distant port horns — the alive-world staple you hear before you see.
// Meridian's soundscape is one synthesized two-tone horn from somewhere
// past the apron every minute or so: WebAudio oscillators, no assets, no
// loop file to cache-bust. Deliberately quiet — texture, not signal.
//
// Same autoplay contract as ClubAudio: nothing sounds until the page has
// seen a user gesture (unlock()), and a headless capture never gestures,
// so the harness stays silent.

const MIN_GAP_MS = 35_000;
const EXTRA_GAP_MS = 55_000;

export class PortAmbience {
  private ctx: AudioContext | undefined;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private active = false;
  private unlocked = false;

  /** Call from a user-gesture handler; safe to call repeatedly. */
  unlock(): void {
    if (this.unlocked) return;
    this.unlocked = true;
    if (this.active) this.schedule();
  }

  /** The horns blow only on Meridian's surface; elsewhere they stop. */
  setActive(active: boolean): void {
    if (this.active === active) return;
    this.active = active;
    if (active && this.unlocked) this.schedule();
    if (!active && this.timer !== undefined) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }
  }

  private schedule(): void {
    if (this.timer !== undefined) clearTimeout(this.timer);
    this.timer = setTimeout(
      () => {
        this.blow();
        if (this.active) this.schedule();
      },
      MIN_GAP_MS + Math.random() * EXTRA_GAP_MS,
    );
  }

  /** One horn: two low tones swelling and dying over a few seconds. */
  private blow(): void {
    if (!this.active) return;
    this.ctx ??= new AudioContext();
    const ctx = this.ctx;
    if (ctx.state !== "running") {
      void ctx.resume();
      return; // next scheduled blow will land
    }
    const now = ctx.currentTime;
    for (const [freq, peak, duration] of [
      [65.4, 0.045, 2.6],
      [98.0, 0.03, 2.0],
    ] as const) {
      const osc = ctx.createOscillator();
      osc.type = "triangle";
      osc.frequency.value = freq * (0.995 + Math.random() * 0.01);
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(peak, now + 0.6);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
      // A touch of lowpass keeps the triangle's edge from reading "synth".
      const filter = ctx.createBiquadFilter();
      filter.type = "lowpass";
      filter.frequency.value = 420;
      osc.connect(gain);
      gain.connect(filter);
      filter.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + duration + 0.1);
    }
  }
}
